/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import SelectMenu from '../../../../component/common/SelectMenu';
import Pagination from '../../../../component/common/table2/Pagination';
import { useCompanySectionFilter } from '../../../../component/B2B/CompanySectionLayout';
import { SidebarContext } from '../../../../context/SidebarContext';
import { getLocalStorageItem } from '../../../../utils/helper';
import {
  Panel,
  PanelEmpty,
  PanelTable,
  Chip,
  StandardButton,
  PANEL_ROW_CLASS
} from '../../../../component/common/designSystem';
import { challengePhaseChipClass } from './challengesTheme';
import { fetchCompanyChallengesList, formatBigNumber } from './companyChallengesApi';

const PER_PAGE = 10;

// '' = every phase; the value is passed straight through to the API.
const PHASES = [
  { name: 'All phases', value: '' },
  { name: 'Running', value: 'running' },
  { name: 'Upcoming', value: 'upcoming' },
  { name: 'Ended', value: 'ended' },
  { name: 'Draft', value: 'draft' }
];

const dateRange = (start, end) => {
  const format = (value) =>
    value
      ? new Date(value).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
      : '-';
  return `${format(start)} – ${format(end)}`;
};

/**
 * Company Challenges → Challenges.
 *
 * One row per challenge this company authored, carrying the participation and
 * tracked volume its OWN people produced — the same challenge seen by the
 * management page, scored against the population the section is scoped to.
 *
 * A row opens the existing per-challenge leaderboard on the management side
 * rather than duplicating it here.
 */
const CChallengesList = () => {
  const navigate = useNavigate();
  const { isB2BFansMode } = useContext(SidebarContext);
  const { department } = useCompanySectionFilter();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const [loader, setLoader] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [phase, setPhase] = useState('');
  const [volumeLabel, setVolumeLabel] = useState('Steps');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      const response = await fetchCompanyChallengesList({
        page,
        perPage: PER_PAGE,
        search,
        phase,
        department
      });
      setRows(response.data);
      setTotalCount(response.totalCount);
      if (response.primaryDataTypeLabel) setVolumeLabel(response.primaryDataTypeLabel);
      setLoader(false);
    },
    [phase, department, isB2BFansMode]
  );

  // Debounced fetch on search / page / filter change.
  useEffect(() => {
    const timeout = setTimeout(() => fetchData(currentPage, searchTerm), 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  // A narrower filter can strand you on a page that no longer exists.
  useEffect(() => setCurrentPage(1), [phase, department, isB2BFansMode]);

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Challenges — Performance | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="w-full sm:w-44">
          <SelectMenu
            menuList={PHASES}
            showLabel={false}
            defaultSelected={PHASES.find((entry) => entry.value === phase)}
            setSelectedMenu={(entry) => setPhase(entry.value)}
          />
        </div>
        <SearchInput
          id="company-challenges-search"
          name="company-challenges-search"
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search challenges"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <Panel>
        {rows.length ? (
          <PanelTable
            head={
              <>
                <th className="py-2 pr-3 font-medium">Challenge</th>
                <th className="py-2 pr-3 font-medium">Dates</th>
                <th className="py-2 pr-3 font-medium text-right">Joined</th>
                <th className="py-2 pr-3 font-medium text-right">Completed</th>
                <th className="py-2 pr-3 font-medium text-right">{volumeLabel}</th>
                <th className="py-2 pr-3 font-medium text-right">Targets</th>
                <th className="py-2 pr-3 font-medium text-right">Rewards</th>
                <th className="py-2 font-medium text-right" />
              </>
            }
          >
            {rows.map((row) => (
              <tr key={row._id} className={PANEL_ROW_CLASS}>
                <td className="py-3 pr-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {row.image ? (
                      <img
                        src={row.image}
                        alt=""
                        className="w-9 h-9 rounded-input object-cover shrink-0"
                      />
                    ) : null}
                    <div className="min-w-0">
                      <p className="truncate font-medium" title={row.title}>
                        {row.title}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Chip className={challengePhaseChipClass(row.phase)}>{row.phaseLabel}</Chip>
                        <span className="text-xs text-gray-400">
                          {row.taskCount} task{row.taskCount === 1 ? '' : 's'}
                          {row.trackedTaskCount > 0 ? ` · ${row.trackedTaskCount} tracked` : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {dateRange(row.startDate, row.endDate)}
                </td>
                <td className="py-3 pr-3 text-right">{row.participants.toLocaleString()}</td>
                <td className="py-3 pr-3 text-right">
                  {row.completed.toLocaleString()}
                  <span className="ml-1 text-xs text-gray-400">({row.completionRate}%)</span>
                </td>
                <td className="py-3 pr-3 text-right">
                  {formatBigNumber(row.volume)}
                  {row.participants > 0 ? (
                    <span className="ml-1 text-xs text-gray-400">
                      ({formatBigNumber(row.avgVolumePerParticipant)}/person)
                    </span>
                  ) : null}
                </td>
                <td className="py-3 pr-3 text-right">{row.targetHitRate}%</td>
                <td className="py-3 pr-3 text-right">
                  {row.rewards > 0 ? (
                    <>
                      {row.winners}/{row.winnerSlots}
                      <span className="ml-1 text-xs text-gray-400">won</span>
                    </>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="py-3 text-right">
                  {/* One challenge's leaderboard already exists on the
                      management side (NewChallenges → Summary tab), which
                      reads the id off router state — link there instead of
                      rebuilding it. */}
                  <StandardButton
                    text="View"
                    variant="secondary"
                    onClick={() =>
                      navigate(
                        `/${slug}/challenges/create-new-challenges?page=summary&newchallenge=false`,
                        { state: { _id: row._id } }
                      )
                    }
                  />
                </td>
              </tr>
            ))}
          </PanelTable>
        ) : (
          <PanelEmpty>
            {searchTerm || phase
              ? 'No challenges match this filter.'
              : 'No challenges created yet.'}
          </PanelEmpty>
        )}
      </Panel>

      <div className="mt-5">
        {totalCount > PER_PAGE ? (
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={PER_PAGE}
            onPageChange={(page) => setCurrentPage(page)}
          />
        ) : (
          <span />
        )}
      </div>
    </div>
  );
};

export default CChallengesList;
